import { Chip, Divider, Typography } from '@mui/material';
import React, { useState } from 'react';
import Dialog from '../../../../dialog/Dialog';
import isEmpty from 'lodash/isEmpty';
import { validate } from '../../../../../utils';
import { Alert } from '@mui/material';
import Editor from '../../../../FormControl/Editor';
import ButtonStartIcon from '../../../../button/ButtonStartIcon';
import { useParams } from 'react-router-dom';
import useWorkedExamples from '../../../../../hooks/developmental-rubrics/useWorkedExamples';
import { handlerErrorV2 } from '../../../../../utils/errors';
import ChipMedia from '../../../../chip/ChipMedia';
import useUpload from '../../../../../hooks/upload/useUpload';
import useGranularSteps from '../../../../../hooks/developmental-rubrics/useGranularStep';
import DialogAttachMedia from '../../../../dialog/DialogAttachMedia';
import ReadOnlyTextEditor from '../../../../TextEditor/ReadOnlyTextEditor';

const DialogWorkedExamples = ({ onClose, open, formData, onSave, type, element = 'worked-examples' }) => {
  const { id, curriculumId } = useParams();
  const types = [
    'create',
    'edit',
    'delete',
    'create-granular-step',
    'edit-granular-step',
    'delete-granular-step'
  ];
  const [form, setForm] = useState(formData);
  const [errors, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('');
  const [modal, setModal] = useState(null);
  const { createWorkedExample, updateWorkedExample, deleteWorkedExample } = useWorkedExamples();

  const { deleteFiles, uploadFile } = useUpload();

  const {
    createWorkedExampleForGranularStep,
    updateWorkedExampleForGranularStep,
    deleteWorkedExampleForGranularStep
  } = useGranularSteps();

  const onChange = (key, value) => {
    setForm({ ...form, [key]: value });
    setError({ ...errors, [key]: null });
  };

  const onConfirm = async (e) => {
    e.preventDefault();
    if (element !== 'worked-examples') {
      setError({ message: 'Invalid element' });
      return;
    }
    if (!types.includes(type)) {
      setError({ message: 'Invalid type' });
      return;
    }
    const fieldRules = {};
    const errors = validate(fieldRules, form);
    if (!isEmpty(errors)) {
      setError(errors);
    } else {
      if (id) {
        setLoading(true);
        try {
          let result = { error: false };
          if (['create', 'edit', 'create-granular-step', 'edit-granular-step'].includes(type)) {
            const media = form.media
              ?.filter((item) => item.type === 'file' && item.file)
              ?.map((item) => ({
                ...item,
                id: item._id || item.id,
                type: item.mimetype
              }));

            if (media?.length > 0) {
              const resultUpload = await uploadFile(media, 'uploads');
              if (resultUpload.error) {
                setError(resultUpload);
                return;
              }
              const newMedia = resultUpload.data;
              form.media = form.media
                .map((item) => {
                  if (item.type === 'file') {
                    const find = newMedia.find((media) => media.id === item.id);
                    if (find) {
                      return { ...item, ...find };
                    }
                  }

                  return item;
                })
                .filter((item) => item.link);
            }
          }
          if (['delete-granular-step', 'delete'].includes(type)) {
            const media = form.media.filter((item) => item.type === 'file');

            if (media.length > 0) {
              const resultDelete = await deleteFiles(media, 'uploads');
              if (resultDelete.error) {
                setError(resultDelete);
                return;
              }
            }
          }

          if (type === 'create') {
            result = await createWorkedExample(form);
          }
          if (type === 'edit') {
            result = await updateWorkedExample(form._id, form);
          }
          if (type === 'delete') {
            result = await deleteWorkedExample(form._id);
          }

          if (type === 'create-granular-step') {
            result = await createWorkedExampleForGranularStep(curriculumId, id, form.lcId, form.gsId, form);
          }

          if (type === 'edit-granular-step') {
            result = await updateWorkedExampleForGranularStep(
              curriculumId,
              id,
              form.lcId,
              form.gsId,
              form._id,
              form
            );
          }

          if (type === 'delete-granular-step') {
            result = await deleteWorkedExampleForGranularStep(
              curriculumId,
              id,
              form.lcId,
              form.gsId,
              form._id
            );
          }

          if (result.error) {
            setError(result);
          } else {
            onClose();
          }
        } catch (error) {
          const message = handlerErrorV2(error);
          setError({ message });
        } finally {
          setLoading(false);
        }
        // setError({ message: 'Invalid id' })
      } else {
        onSave(form, type);
        onClose();
      }
    }
  };

  const onSaveMedia = async (type, data) => {
    const currentMedia = form.media || [];
    if (type === 'add') {
      setForm({
        ...form,
        media: [
          ...currentMedia,
          {
            id: data._id || new Date().getTime(),
            ...data
          }
        ]
      });
    } else {
      const media = currentMedia
        .map((item) => ({ id: item._id || item.id, ...item }))
        .filter((item) => item.id !== data.id);
      setForm({ ...form, media });
      if (data.type === 'file') {
        const result = await deleteFiles([data], 'uploads');
        if (result.error) {
          setError(result);
        }
      }
    }
  };
  const onMode = () => {
    if (isEmpty(form.comment)) {
      setMode('');
    } else {
      setMode('editing');
    }
  };

  const Actions = (
    <div className='w-full flex gap-4 justify-between'>
      <div className='flex gap-4 justify-end items-center'>
        {types.includes(type) && type !== 'delete' && type !== 'delete-granular-step' && (
          <>
            <ButtonStartIcon
              onClick={() => setModal({ form: {}, type: 'add' })}
              color='primary'
              variant='text'
              disabled={loading}
              startIcon={'link'}
            >
              Attach media
            </ButtonStartIcon>
            {mode && (
              <>
                <Divider orientation='vertical' flexItem />
                <Chip
                  label={mode}
                  color={mode === 'editing' ? 'primary' : 'inherit'}
                  className='capitalize font-bold'
                />
              </>
            )}
          </>
        )}
      </div>
      <div className='flex gap-4 justify-end'>
        <ButtonStartIcon
          onClick={onClose}
          color='primary'
          variant='outlined'
          disabled={loading}
          startIcon={['fal', 'times']}
        >
          Cancel
        </ButtonStartIcon>
        {types.includes(type) && (
          <ButtonStartIcon
            onClick={onConfirm}
            color='primary'
            variant='contained'
            disabled={loading}
            loading={loading}
            startIcon={['fal', 'check']}
          >
            {['delete', 'delete-granular-step'].includes(type) ? 'Confirm' : 'Save'}
          </ButtonStartIcon>
        )}
      </div>
    </div>
  );
  const title = type?.indexOf('-granular-step') > -1 ? type?.replace('-granular-step', '') : type;
  return (
    <>
      <Dialog
        id={`add-update-delete-${element}`}
        show={open}
        title={`${title} ${element?.replace(/-/g, ' ')}`}
        onCloseHeader={['preview-granular-step'].includes(type) ? onClose : null}
        maxWidth={['delete', 'delete-granular-step'].includes(type) ? 'xs' : 'md'}
        actions={['preview-granular-step'].includes(type) ? null : Actions}
        onClose={onClose}
      >
        {['delete', 'delete-granular-step'].includes(type) ? (
          <Typography>Are you sure you want to delete the worked example?</Typography>
        ) : ['preview-granular-step'].includes(type) ? (
          <>
            <div className='flex flex-col gap-4'>
              <ReadOnlyTextEditor content={form.comment} placeholder='Start entering your text' />
              {form.media && form.media.length > 0 && (
                <div className='flex flex-wrap gap-4 items-center'>
                  {form.media.map((data, index) => {
                    return (
                      <ChipMedia key={`media-${index}-${data._id}-${data.type}`} index={index} data={data} />
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className='flex flex-col gap-4'>
            <Editor
              value={form.comment}
              onChange={(value) => onChange('comment', value)}
              readOnly={loading}
              placeholder='Start entering your text'
              onKeyPress={() => onMode()}
              onFocus={() => onMode()}
              onBlur={() => onMode()}
            />
            {form.media && form.media.length > 0 && (
              <div className='flex flex-wrap gap-4 items-center'>
                {form.media.map((data, index) => {
                  return (
                    <ChipMedia
                      key={`media-${index}-${data._id}-${data.type}`}
                      index={index}
                      data={data}
                      disabled={loading}
                      onDelete={() =>
                        setModal({
                          type: 'delete',
                          form: {
                            id: data._id || data.id,
                            ...data
                          }
                        })
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
        {errors && errors.message && (
          <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
            {errors.message}
          </Alert>
        )}
      </Dialog>
      {modal && (
        <DialogAttachMedia
          open={!!modal}
          onClose={() => setModal(false)}
          formData={modal.form}
          onSave={(data) => onSaveMedia(modal.type, data)}
          type={modal.type}
        />
      )}
    </>
  );
};

export default DialogWorkedExamples;
